import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT } from "@/data/content";

const INFO = [
  { icon: Mail, label: "E-Mail", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Phone, label: "Telefon", value: CONTACT.phone, href: CONTACT.phoneHref },
  { icon: MapPin, label: "Adresse", value: CONTACT.address, href: null },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Bitte füllen Sie Name, E-Mail und Nachricht aus.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/contact.php", form);
      toast.success(data.message || "Vielen Dank! Ihre Anfrage wurde gesendet.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: heading + info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-400">
            Kontakt
          </span>
          <h2 className="font-display font-semibold tracking-tight text-4xl md:text-6xl text-zinc-950 mt-4 leading-[1.02]">
            Reden wir über <span className="brand-text">Ihr Projekt</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg text-zinc-600 leading-relaxed max-w-md">
            Erzählen Sie uns kurz, worum es geht — wir melden uns
            zeitnah mit einer ehrlichen Einschätzung.
          </p>

          <div className="mt-10 space-y-5">
            {INFO.map((item) => {
              const Icon = item.icon;
              const content = (
                <div className="flex items-center gap-4 group">
                  <span className="flex-shrink-0 w-11 h-11 rounded-full border border-zinc-200 flex items-center justify-center group-hover:border-zinc-900 transition-colors duration-200">
                    <Icon className="w-4 h-4 text-zinc-700" />
                  </span>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                      {item.label}
                    </div>
                    <div className="text-zinc-900 font-medium">{item.value}</div>
                  </div>
                </div>
              );
              return item.href ? (
                <a key={item.label} href={item.href} data-testid={`contact-info-${item.label.toLowerCase()}`}>
                  {content}
                </a>
              ) : (
                <div key={item.label} data-testid={`contact-info-${item.label.toLowerCase()}`}>
                  {content}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 bg-white rounded-2xl border border-zinc-200 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.15)] p-6 md:p-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-700">Name *</Label>
              <Input
                id="name" name="name" value={form.name} onChange={update}
                data-testid="contact-name" placeholder="Max Mustermann"
                className="h-12 border-zinc-200 focus-visible:ring-[#00A8E8]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-700">E-Mail *</Label>
              <Input
                id="email" name="email" type="email" value={form.email} onChange={update}
                data-testid="contact-email" placeholder="max@firma.at"
                className="h-12 border-zinc-200 focus-visible:ring-[#00A8E8]"
              />
            </div>
          </div>
          <div className="space-y-2 mt-6">
            <Label htmlFor="phone" className="text-zinc-700">Telefon</Label>
            <Input
              id="phone" name="phone" value={form.phone} onChange={update}
              data-testid="contact-phone" placeholder="+43 ..."
              className="h-12 border-zinc-200 focus-visible:ring-[#00A8E8]"
            />
          </div>
          <div className="space-y-2 mt-6">
            <Label htmlFor="message" className="text-zinc-700">Nachricht *</Label>
            <Textarea
              id="message" name="message" value={form.message} onChange={update}
              data-testid="contact-message" rows={5}
              placeholder="Worum geht es? Erzählen Sie uns von Ihrem Vorhaben ..."
              className="border-zinc-200 focus-visible:ring-[#00A8E8] resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="group relative mt-8 w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-zinc-950 text-white text-sm font-medium px-8 py-4 overflow-hidden transition-colors duration-200 disabled:opacity-70"
          >
            <span className="absolute inset-0 brand-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative flex items-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet ...
                </>
              ) : (
                <>
                  Anfrage senden
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                </>
              )}
            </span>
          </button>
        </motion.form>
      </div>
    </section>
  );
};
